<div class="admin-panel-container">
    <!-- Main Content Area -->
    <div class="main-content">
        <!-- Access Denied Message -->
        <div *ngIf="isAccessDenied && !isLoadingRules" class="access-denied-container">
            <div class="authorization-message">
                <div class="message-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <div class="message-content">
                    <h3 class="message-title">Access Denied</h3>
                    <p class="message-text">You do not have permission to access the Admin Panel. This feature requires <strong>ADMIN_PERMISSION_LIST</strong> permission. Please contact your administrator if you need access.</p>
                </div>
            </div>
        </div>

        <!-- Loader -->
        <div *ngIf="isLoadingRules" class="loader-wrapper">
            <div class="loader-container">
                <div class="spinner-loader"></div>
            </div>
        </div>

        <!-- Empty State - Optimized Design (only show when not loading and access is granted) -->
        <div *ngIf="!isLoadingRules && !isAccessDenied && rules.length === 0" class="empty-state-wrapper">
            <div class="version-history-container">
                <div class="litmus-intraction-container">
                    <div class="admin-header-section">
                        <div class="drafts-header">
                            <div class="header-text">
                                <h3>Admin Panel</h3>
                                <p class="page-description">Responsible For Providing Access Control To Settings Tabs
                                    And Managing User Permissions</p>
                                <button class="create-btn" [class.disabled]="!canCreateNewRule()"
                                    [disabled]="!canCreateNewRule()" [title]="getDisabledButtonTooltip()"
                                    (click)="openSidePanel()">
                                    <span>Create New Rule</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Empty State Card -->
                <div class="empty-state-container">
                    <div class="empty-state-message">
                        No Rules created. Click 'Create New Rules' to add your first one.
                    </div>
                </div>
            </div>
        </div>

        <!-- Rules List (only show when not loading and access is granted) -->
        <div *ngIf="!isLoadingRules && !isAccessDenied && rules.length > 0" class="version-history-container">
            <div class="litmus-intraction-container">
                <!-- Header Section -->
                <div class="admin-header-section">
                    <div class="drafts-header">
                        <div class="header-text">
                            <h3>Admin Panel</h3>
                            <p class="page-description">Responsible For Providing Access Control To Settings Tabs And
                                Managing User Permissions</p>
                        </div>
                        <div class="header-actions-row">
                            <button class="create-btn" [class.disabled]="!canCreateNewRule()"
                                [disabled]="!canCreateNewRule()" [title]="getDisabledButtonTooltip()"
                                (click)="openSidePanel()">
                                <span>Create New Rule</span>
                            </button>
                            <div class="view-toggle">
                                <button class="toggle-btn" [class.active]="isCardView()" (click)="setViewMode('card')"
                                    title="Card View">
                                    <svg class="icon-svg">
                                        <use xlink:href="assets/images/icons.svg#view-grid"></use>
                                    </svg>
                                </button>
                                <button class="toggle-btn" [class.active]="isTableView()" (click)="setViewMode('table')"
                                    title="Table View">
                                    <svg class="icon-svg">
                                        <use xlink:href="assets/images/icons.svg#view-list"></use>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Card View -->
            <div *ngIf="isCardView()" class="rules-grid">
                <div *ngFor="let r of rules" class="rule-card">
                    <!-- Card Header -->
                    <div class="card-header">
                        <h3 class="rule-title">{{ r.title }}</h3>
                        <button class="btn-edit" (click)="onEditRule(r)" title="Edit">
                            <svg class="icon-svg">
                                <use xlink:href="assets/images/icons.svg#editIcon"></use>
                            </svg>
                        </button>
                    </div>

                    <!-- Top Info Row -->
                    <div class="card-top-info">
                        <div class="info-item">
                            <span class="info-label">Permission type</span>
                            <span class="permission-badge" [class]="'badge-' + r.permission_type.toLowerCase()">
                                {{ r.permission_type }}
                            </span>
                        </div>
                        <div class="info-item">
                            <span class="info-label">Settings count</span>
                            <span class="info-value">{{ r.permission_list.length }}</span>
                        </div>
                        <div class="info-item">
                            <span class="info-label">Access Type</span>
                            <span class="info-value">{{ getHierarchyDisplayText(r.user_type_for_permission) }}</span>
                        </div>
                    </div>

                    <!-- Created/Updated Section -->
                    <div class="card-meta-section">
                        <div class="meta-item">
                            <span class="meta-label">Created by</span>
                            <div class="meta-value-group">
                                <span class="meta-email">{{ r.created_by.name || r.created_by.email_address }}</span>
                                <span class="meta-date">{{ r.created_date | date: 'MMM dd, yyyy HH:mm' }}</span>
                            </div>
                        </div>
                        <div class="meta-item" *ngIf="r.last_updated_by">
                            <span class="meta-label">Updated by</span>
                            <div class="meta-value-group">
                                <span class="meta-email">{{ r.last_updated_by.name || r.last_updated_by.email_address
                                    }}</span>
                                <span class="meta-date">{{ r.last_updated_by.updated_date | date: 'MMM dd, yyyy HH:mm'
                                    }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- User Chips Section -->
                    <div class="card-chips-section" *ngIf="r.custom_list && r.custom_list.length > 0">
                        <span class="chips-label">User</span>
                        <div class="chips-container user-chips-container">
                            <span class="user-chip"
                                *ngFor="let email of r.custom_list.slice(0, isUsersExpanded(r._id) ? r.custom_list.length : 2)">
                                {{ email }}
                            </span>
                            <span class="user-chip more clickable" *ngIf="r.custom_list.length > 2"
                                (click)="toggleUsersExpansion(r._id)">
                                {{ isUsersExpanded(r._id) ? 'Show less' : '+ ' + (r.custom_list.length - 2) + ' more' }}
                            </span>
                        </div>
                    </div>

                    <!-- Settings Type Chips Section -->
                    <div class="card-chips-section" *ngIf="r.permission_list.length > 0">
                        <span class="chips-label">Settings type</span>
                        <div class="chips-container settings-chips-container">
                            <span class="setting-chip"
                                *ngFor="let setting of r.permission_list.slice(0, isSettingsExpanded(r._id) ? r.permission_list.length : 2)">
                                {{ getItemDescriptionByKey(setting, r) || setting }}
                            </span>
                            <span class="setting-chip more clickable" *ngIf="r.permission_list.length > 2"
                                (click)="toggleSettingsExpansion(r._id)">
                                {{ isSettingsExpanded(r._id) ? 'Show less' : '+ ' + (r.permission_list.length - 2) + '
                                more' }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Table View -->
            <div *ngIf="isTableView()" class="table-responsive rules-table">
                <table>
                    <thead>
                        <tr>
                            <th>Title</th>
                            <th>Permission Type</th>
                            <th>Access Type</th>
                            <th>Setting Count</th>
                            <th>Created By</th>
                            <th>Updated By</th>
                            <th class="action-header-cell">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let r of rules; let i = index">
                            <td>{{ r.title }}</td>
                            <td class="status-tag-container">
                                <span *ngIf="r.permission_type === 'READ'" class="read-tag status-tag">Read</span>
                                <span *ngIf="r.permission_type === 'WRITE'" class="write-tag status-tag">Write</span>
                            </td>
                            <td>{{ getHierarchyDisplayText(r.user_type_for_permission) }}</td>
                            <td class="setting-count-cell">{{ r.permission_list.length }}</td>
                            <td>{{ r.created_by.email_address }}</td>
                            <td>{{ r.last_updated_by ? r.last_updated_by.email_address : '-' }}</td>
                            <td class="action-column-cell">
                                <button (click)="onEditRule(r)" class="edit-button">Edit</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>

    <!-- Admin Panel Side Panel -->
    <div class="admin-side-panel" *ngIf="isSidePanelOpen">
        <div class="side-panel-overlay" (click)="closeSidePanel()"></div>
        <div class="side-panel-container">
            <div class="side-panel-header">
                <div class="header-label">{{ isEditMode ? 'Edit Rule' : 'Create New Rule' }}</div>
                <div class="header-actions">
                    <button class="close-button" (click)="closeSidePanel()">
                        <img src="./assets/images/close.svg">
                    </button>
                </div>
            </div>

            <div class="side-panel-content">
                <!-- Error Messages -->
                <div *ngIf="formErrors.length > 0" class="error-alert">
                    <svg class="icon-svg me-2">
                        <use xlink:href="assets/images/icons.svg#toast-warning-icon"></use>
                    </svg>
                    {{ formErrors[0] }}
                </div>

                <!-- Form Fields -->
                <div class="form-section">
                    <!-- Rule Name Row -->
                    <div class="form-group form-row-horizontal">
                        <label class="form-label col-label">Rule Name</label>
                        <div class="col-input">
                            <input type="text" class="form-control modern-input" placeholder="Ente a rule name"
                                [(ngModel)]="ruleTitle" [class.error]="hasTitleError()" [maxlength]="maxRuleNameLength">
                            <div class="char-count-info" *ngIf="ruleTitle.trim().length >= maxRuleNameLength">
                                <span class="warning">
                                    {{ ruleTitle.trim().length }} / {{ maxRuleNameLength }} characters
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Settings Selection Dropdown -->
                    <div class="form-group form-row-horizontal">
                        <label class="form-label col-label">Setting Configuration</label>
                        <div class="col-input">
                            <div class="modern-dropdown" [class.open]="isDropdownOpen" #settingsDropdownRef>
                                <button type="button" class="dropdown-trigger" (click)="toggleDropdown()"
                                    [class.error]="hasSettingError()">
                                    <div class="trigger-content">
                                        <span class="trigger-text">
                                            {{ addedItems.length > 0 ? 'Selected Settings' : 'Select' }}
                                        </span>
                                        <span class="trigger-badge" *ngIf="getTotalSelectedCount() > 0">
                                            {{ getTotalSelectedCount() }}
                                        </span>
                                    </div>
                                    <svg class="icon-svg trigger-icon">
                                        <use xlink:href="assets/images/icons.svg#chevron-down"></use>
                                    </svg>
                                </button>

                                <!-- Dropdown Menu -->
                                <div class="dropdown-menu modern-dropdown-menu" [class.show]="isDropdownOpen"
                                    *ngIf="isDropdownOpen">
                                    <div class="dropdown-content">
                                        <div class="settings-list">
                                            <ng-container *ngFor="let key of categoryOrder">
                                                <div class="settings-group" *ngIf="settings[key]">
                                                    <div class="group-title">{{ settings[key].title }}</div>
                                                    <div class="group-items">
                                                        <label class="simple-checkbox"
                                                            *ngFor="let item of settings[key].list_of_items">
                                                        <input type="checkbox" [id]="key + '_' + item.key"
                                                            [checked]="isItemSelected(key, item.key)"
                                                            (change)="onItemChange(key, item.key, $any($event.target).checked)">
                                                        <span class="checkbox-text">{{ item.description }}</span>
                                                    </label>
                                                    </div>
                                                </div>
                                            </ng-container>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Permission Type Dropdown -->
                    <div class="form-group form-row-horizontal">
                        <label class="form-label col-label">Permission Type</label>
                        <div class="col-input">
                            <div class="modern-dropdown" [class.open]="isPermissionDropdownOpen" #permissionDropdownRef>
                                <button type="button" class="dropdown-trigger" (click)="togglePermissionDropdown()"
                                    [class.error]="hasPermissionError()">
                                    <div class="trigger-content">
                                        <span class="trigger-text">
                                            {{ selectedPermissionType || 'Select' }}
                                        </span>
                                    </div>
                                    <svg class="icon-svg trigger-icon">
                                        <use xlink:href="assets/images/icons.svg#chevron-down"></use>
                                    </svg>
                                </button>
                                <div class="dropdown-menu modern-dropdown-menu" [class.show]="isPermissionDropdownOpen"
                                    *ngIf="isPermissionDropdownOpen">
                                    <div class="dropdown-header">
                                        <h4>Permission Type</h4>
                                        <p>Choose the level of access for this rule</p>
                                    </div>
                                    <div class="dropdown-content">
                                        <label class="modern-radio" *ngFor="let opt of permissionTypeOptions">
                                            <input type="radio" name="permissionType" [id]="'perm_' + opt"
                                                [checked]="selectedPermissionType === opt"
                                                (change)="selectPermissionType(opt)">
                                            <span class="radio-mark"></span>
                                            <span class="radio-label">
                                                <strong>{{ opt }}</strong>
                                                <small class="radio-description">
                                                    {{ opt === 'READ' ? 'View only access' : opt === 'WRITE' ? 'Modify
                                                    and create access' : 'Full read and write access' }}
                                                </small>
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Hierarchy Access Dropdown -->
                    <div class="form-group form-row-horizontal">
                        <label class="form-label col-label">Hierarchy Access</label>
                        <div class="col-input">
                            <div class="modern-dropdown" [class.open]="isHierarchyDropdownOpen" #hierarchyDropdownRef>
                                <button type="button" class="dropdown-trigger" (click)="toggleHierarchyDropdown()"
                                    [class.error]="hasHierarchyError()">
                                    <div class="trigger-content">
                                        <span class="trigger-text">
                                            {{ selectedHierarchyOption || 'Select' }}
                                        </span>
                                    </div>
                                    <svg class="icon-svg trigger-icon">
                                        <use xlink:href="assets/images/icons.svg#chevron-down"></use>
                                    </svg>
                                </button>
                                <div class="dropdown-menu modern-dropdown-menu" [class.show]="isHierarchyDropdownOpen"
                                    *ngIf="isHierarchyDropdownOpen">
                                    <div class="dropdown-header">
                                        <h4>Access Scope</h4>
                                        <p>Define the organisational scope for this rule</p>
                                    </div>
                                    <div class="dropdown-content">
                                        <label class="modern-radio" [class.selected]="selectedHierarchyOption === opt"
                                            *ngFor="let opt of hierarchyOptions">
                                            <input type="radio" name="hierarchyOption" [id]="'hier_' + opt"
                                                [checked]="selectedHierarchyOption === opt"
                                                (change)="selectHierarchyOption(opt)">
                                            <span class="radio-mark"></span>
                                            <span class="radio-label">
                                                <strong>{{ opt }}</strong>
                                                <small class="radio-description">
                                                    {{ opt === 'Hierarchy Level Access' ? 'Access based on
                                                    organisational level' :
                                                    opt === 'Hierarchy Value Access' ? 'Access based on specific values'
                                                    :
                                                    'Access for specific email addresses' }}
                                                </small>
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Custom Emails Section -->
                    <div class="form-group form-row-horizontal" *ngIf="selectedHierarchyOption === 'Custom'">
                        <label class="form-label col-label">Custom Email Addresses</label>
                        <div class="col-input">
                            <input type="text" class="form-control modern-input"
                                placeholder="Enter email addresses (separate with commas or press Enter)"
                                [(ngModel)]="customEmailInput" (keydown)="onCustomEmailKeyDown($event)"
                                [class.error]="customEmailError"
                                [disabled]="customEmails.length >= maxCustomEmailsLimit">

                            <div class="error-message" *ngIf="customEmailError">
                                <svg class="icon-svg me-2">
                                    <use xlink:href="assets/images/icons.svg#toast-warning-icon"></use>
                                </svg>
                                {{ customEmailError }}
                            </div>
                            <div class="email-count-info" *ngIf="customEmails.length > 0">
                                <span [class.warning]="customEmails.length >= maxCustomEmailsLimit">
                                    {{ customEmails.length }} / {{ maxCustomEmailsLimit }} email addresses
                                </span>
                            </div>
                            <div class="email-chips" *ngIf="customEmails.length > 0">
                                <span class="email-chip" *ngFor="let em of customEmails">
                                    <svg class="icon-svg me-2">
                                        <use xlink:href="assets/images/icons.svg#mail"></use>
                                    </svg>
                                    {{ em }}
                                    <button type="button" class="chip-remove" (click)="removeCustomEmail(em)">
                                        <svg class="icon-svg">
                                            <use xlink:href="assets/images/icons.svg#close"></use>
                                        </svg>
                                    </button>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Warning Message -->
                <div class="warning-message" *ngIf="addedItems.length === 0 && showNoSelectionMessage">
                    <svg class="icon-svg me-2">
                        <use xlink:href="assets/images/icons.svg#toast-warning-icon"></use>
                    </svg>
                    Please select at least one setting from the dropdown above
                </div>
            </div>

            <div class="side-panel-footer">
                <button class="btn-cancel" (click)="closeSidePanel()">Cancel</button>
                <button class="btn-save" (click)="onCreateRule()" [disabled]="!isFormValidForSubmission()"
                    type="button">
                    {{ isEditMode ? 'Update Rule' : 'Create Rule' }}
                </button>
            </div>
        </div>
    </div>
</div>